/**
 * Global 401 interceptor — redirect to Login on session timeout.
 *
 * The v2 pages each fetch directly with window.fetch (not the api.js helper),
 * so there was no single place that noticed an expired session. This installs a
 * one-time wrapper around window.fetch: when an AUTHENTICATED request to our own
 * API comes back 401, the JWT has expired — we flag it and dispatch
 * `fc:auth-expired`, which App.js already listens for to clear auth and show the
 * LoginScreen.
 *
 * Guards (so we never log the user out wrongly):
 *   • only our API base — ignore third-party fetches
 *   • skip /auth/login — a 401 there means "wrong password", not a dead session
 *   • only when a token is stored — the login screen has none, so no redirect loop
 *   • install once — idempotent
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
let installed = false;

export function installAuthInterceptor() {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await originalFetch(...args);
    try {
      if (res && res.status === 401) {
        const input = args[0];
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const isOurApi = url.startsWith(API_BASE) || url.includes('/api/v1');
        const isLogin = url.includes('/auth/login');
        const hasToken = !!localStorage.getItem('fc_token');
        if (isOurApi && !isLogin && hasToken) {
          try { sessionStorage.setItem('fc_session_expired', '1'); } catch (_) { /* private mode */ }
          window.dispatchEvent(new Event('fc:auth-expired'));
        }
      }
    } catch (_) {
      // Never let the interceptor break a real request.
    }
    return res;
  };
}
